import { LibraryFilterState } from '../hooks/utils';
import { ConfidenceValue } from '../components/skill-stats/skill-stats-card';
import { FilterTopicType } from '../components/filters/digital-skills-topic-filter';
import { ResourceRecommendations } from '../services/learning-api/types/resource.types';
import { RecommendationsGroup } from '../components/recommendations/recommendations-card';

/* FILTER HELPERS */

export const getDistinctTopics = (
  recommendations: ResourceRecommendations[]
): FilterTopicType[] => {
  if (!recommendations || recommendations.length === 0) {
    return [];
  }
  const topics: FilterTopicType[] = [];
  const ensureTopics = (topicToEnsure: FilterTopicType) => {
    if (!topics.some((t) => t.id === topicToEnsure.id)) {
      topics.push(topicToEnsure);
    }
  };

  recommendations.forEach((resource) => {
    resource.specialisms.forEach((topic) => {
      if (resource.recommendationHidden === false) {
        ensureTopics(topic);
      }
    });
  });

  return topics;
};

const matchesTopicFilters = (
  recommendation: ResourceRecommendations,
  selectedTopicFilters: FilterTopicType[]
) => {
  if (selectedTopicFilters.length === 0) {
    return true;
  }

  return recommendation.specialisms.some((specialism: any) =>
    selectedTopicFilters.some((topic) => topic.id === specialism.id)
  );
};

const matchesSearchFilter = (
  recommendation: ResourceRecommendations,
  filters: LibraryFilterState
) => {
  if (!filters.searchQuery || filters.searchQuery.trim() === '') {
    return true;
  }

  return recommendation.title.toLowerCase().includes(filters.searchQuery.toLowerCase());
};

const matchesSkillCategoryFilter = (
  recommendation: ResourceRecommendations,
  filters: LibraryFilterState
) => {
  if (filters.skillCategoryIds.length === 0) {
    return true;
  }

  return !recommendation.skillCategory.some((sc) => !filters.skillCategoryIds.includes(sc.title));
};

const matchesMediaTypeFilter = (
  recommendation: ResourceRecommendations,
  filters: LibraryFilterState
) => {
  if (filters.mediaTypes.length === 0) {
    return true;
  }

  return filters.mediaTypes.includes(recommendation.mediaType);
};

const matchesLocationFilter = (
  recommendation: ResourceRecommendations,
  filters: LibraryFilterState
) => {
  if (filters.locations.length !== 1) {
    return true;
  }

  const selectedLocation = filters.locations[0];
  if (recommendation.isInternal) {
    return selectedLocation === 'Internal';
  }

  return selectedLocation === 'External';
};

export const shouldIncludeRecommendation = (
  recommendation: ResourceRecommendations,
  selectedTopicFilters: FilterTopicType[],
  filters: LibraryFilterState
) => {
  return (
    matchesTopicFilters(recommendation, selectedTopicFilters) &&
    matchesSearchFilter(recommendation, filters) &&
    matchesSkillCategoryFilter(recommendation, filters) &&
    matchesMediaTypeFilter(recommendation, filters) &&
    matchesLocationFilter(recommendation, filters)
  );
};

/* SKILL CATEGORY GROUPS HELPERS */

export type SkillCategoryAssessments = Record<string, { confidence?: ConfidenceValue }>;

type SkillCategoryInfoMap = Record<
  string,
  {
    skill_category_title: string;
    skillGroup: {
      skill_group_id: string;
      title: string;
      colour: string;
    };
  }
>;

const addRecommendationToGroups = (
  groupsMap: Record<string, RecommendationsGroup>,
  recommendation: ResourceRecommendations,
  skillCategoriesInfo: SkillCategoryInfoMap
) => {
  for (const skillCategory of recommendation.skillCategory) {
    const skillCategoryInfo = skillCategoriesInfo[skillCategory.id];
    if (!skillCategoryInfo) {
      continue;
    }
    const groupId = skillCategoryInfo.skillGroup.skill_group_id;
    const group = groupsMap[groupId];

    group.resources.push(recommendation as any);
  }
};

const enrichGroup = (group: RecommendationsGroup) => {
  group.description = group.skills.map((skill) => skill.title).join(', ');

  const averageConfidence = group.skills.reduce((sum, skill) => sum + (skill.confidence || 0), 0);
  group.overallConfidence = Math.floor(
    averageConfidence / Math.max(group.skills.length, 1)
  ) as ConfidenceValue;
};

const createGroupsMap = (
  skillCategoryInfo: SkillCategoryInfoMap,
  assessments: SkillCategoryAssessments
) => {
  const groupsMap: Record<string, RecommendationsGroup> = {};

  Object.entries(skillCategoryInfo).forEach(([id, info]) => {
    const groupId = info.skillGroup.skill_group_id;
    if (!groupsMap[groupId]) {
      groupsMap[groupId] = {
        id: groupId,
        title: info.skillGroup.title,
        description: '',
        accentColor: info.skillGroup.colour,
        skills: [],
        resources: [],
      };
    }
    groupsMap[groupId].skills.push({
      id,
      title: info.skill_category_title,
      confidence: assessments?.[id]?.confidence as ConfidenceValue,
    });
  });
  return groupsMap;
};

export const buildRecommendationGroups = (
  recommendations: ResourceRecommendations[],
  skillCategoriesInfo: SkillCategoryInfoMap,
  assessments: SkillCategoryAssessments,
  selectedTopicFilters: FilterTopicType[],
  filters: LibraryFilterState
): RecommendationsGroup[] => {
  const groupsMap = createGroupsMap(skillCategoriesInfo, assessments);

  for (const recommendation of recommendations) {
    if (!shouldIncludeRecommendation(recommendation, selectedTopicFilters, filters)) {
      continue;
    }

    addRecommendationToGroups(groupsMap, recommendation, skillCategoriesInfo);
  }

  for (const group of Object.values(groupsMap)) {
    enrichGroup(group);
  }

  return Object.values(groupsMap);
};
